import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  Grid
} from '@mui/material';
import {
  DateRange as DateRangeIcon,
  Today as TodayIcon,
  CalendarMonth as CalendarIcon,
  AccessTime as TimeIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useDataProvider } from 'react-admin';

interface DateRangeFilterProps {
  startDate?: Date | null;
  endDate?: Date | null;
  startTime?: string; // HH:mm format for time range filtering
  endTime?: string; // HH:mm format for time range filtering
  onChange: (startDate: Date | null, endDate: Date | null, startTime?: string, endTime?: string) => void;
  disabled?: boolean;
  label?: string;
  showQuickButtons?: boolean;
  customReportingTime?: {
    enabled: boolean;
    businessDayStartTime: string; // HH:mm format
    businessDayEndTime: string; // HH:mm format
    timeZone: string;
    description: string;
    crossMidnightLogic: boolean;
  };
  onReportingTimeChange?: (startTime: string, endTime: string) => void;
  onValidationChange?: (isValid: boolean) => void; // Callback to notify parent of validation state
}

const FilterContainer = styled(Paper)(({ theme }) => ({
  padding: 0,
  marginBottom: 0,
  backgroundColor: 'transparent',
  borderRadius: 0,
  border: 'none',
  boxShadow: 'none',
}));

const DateInputsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'center',
  flexWrap: 'wrap',
}));

const QuickButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  flexWrap: 'wrap',
}));

const BusinessHoursContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[800],
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.grey[600]}`,
}));

const TimePickerContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'center',
  marginTop: theme.spacing(1),
  flexWrap: 'wrap',
}));

const formatDateForInput = (date: Date | string | null): string => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  // Use UTC methods to avoid timezone shifts
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDateFromInput = (dateString: string): Date | null => {
  if (!dateString) return null;
  // Parse as UTC to ensure consistent behavior across timezones
  // "2024-10-17" should always be Oct 17 00:00:00 UTC, not local midnight
  const date = new Date(dateString + 'T00:00:00Z');
  return isNaN(date.getTime()) ? null : date;
};

// Helper to compare if two dates are the same UTC day
const isSameUTCDay = (date1: Date | null, date2: Date | null): boolean => {
  if (!date1 || !date2) return false;
  return date1.getUTCFullYear() === date2.getUTCFullYear() &&
         date1.getUTCMonth() === date2.getUTCMonth() &&
         date1.getUTCDate() === date2.getUTCDate();
};

/** WTD/MTD/YTD end at yesterday 23:59:59.999 UTC (today excluded). */
const getYesterdayEndUtc = (ref: Date = new Date()): Date =>
  new Date(Date.UTC(ref.getUTCFullYear(), ref.getUTCMonth(), ref.getUTCDate() - 1, 23, 59, 59, 999));

/** Week starts Sunday (UTC), same as existing WTD logic. */
const getStartOfWeekSundayUtc = (ref: Date = new Date()): Date => {
  const dayOfWeek = ref.getUTCDay();
  return new Date(Date.UTC(ref.getUTCFullYear(), ref.getUTCMonth(), ref.getUTCDate() - dayOfWeek, 0, 0, 0, 0));
};

// Helper function to format hour number to HH:00 time string
const formatCutoffHour = (hour: number): string => {
  const hourInt = Math.floor(hour); // Handle double values like 2.5 -> 2
  return `${hourInt.toString().padStart(2, '0')}:00`;
};

// const TIME_PRESETS = {
//   breakfast: { label: 'Breakfast', startTime: '08:00', endTime: '11:00' },
//   lunch: { label: 'Lunch', startTime: '12:00', endTime: '17:00' },
//   dinner: { label: 'Dinner', startTime: '17:00', endTime: '23:00' },
// } as const;

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  startDate,
  endDate,
  startTime,
  endTime,
  onChange,
  disabled = false,
  label = 'Date Range',
  showQuickButtons = true,
  customReportingTime,
  onReportingTimeChange,
  onValidationChange
}) => {
  const dataProvider = useDataProvider();
  const [internalStartDate, setInternalStartDate] = useState<Date | null>(startDate || null);
  const [internalEndDate, setInternalEndDate] = useState<Date | null>(endDate || null);
  
  // Read cutoff_hour from tenant attributes, default to 3 if not set
  const [internalStartTime, setInternalStartTime] = useState<string>('03:00');
  const [internalEndTime, setInternalEndTime] = useState<string>('03:00');

  // Load cutoff_hour from tenant attributes via /tenant-dp endpoint
  useEffect(() => {
    const loadCutoffHour = async () => {
      try {
        const { data: tenants } = await dataProvider.getList('tenant', {
          filter: {},
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'name', order: 'ASC' }
        });
        
        if (tenants && tenants.length > 0) {
          const tenant = tenants[0];
          const cutoffHour = tenant?.attributes?.cutoff_hour;
          
          if (typeof cutoffHour === 'number' && cutoffHour >= 0 && cutoffHour <= 23) {
            const formattedTime = formatCutoffHour(cutoffHour);
            setInternalStartTime(formattedTime);
            setInternalEndTime(formattedTime);
            console.log(`✅ [DateRangeFilter] Loaded cutoff_hour from tenant attributes: ${cutoffHour} -> ${formattedTime}`);
          } else {
            console.warn(`⚠️ [DateRangeFilter] Invalid cutoff_hour value: ${cutoffHour}, using default 3`);
          }
        }
      } catch (error) {
        console.warn('⚠️ [DateRangeFilter] Failed to load tenant attributes, using default cutoff_hour: 3', error);
      }
    };
    
    loadCutoffHour();
  }, [dataProvider]);
  
  // Business day time management
  // Read from prop if provided, otherwise fall back to localStorage (from Settings tab)
  const getBusinessDaySettings = () => {
    if (customReportingTime) {
      return {
        startTime: customReportingTime.businessDayStartTime,
        endTime: customReportingTime.businessDayEndTime,
        enabled: customReportingTime.enabled,
      };
    }
    // Fallback to localStorage (set by ReportsSettings)
    return {
      startTime: localStorage.getItem('reports:businessDayStartTime') || '01:00',
      endTime: localStorage.getItem('reports:businessDayEndTime') || '01:00',
      enabled: localStorage.getItem('reports:customTimeEnabled') === 'true',
    };
  };

  const initialSettings = getBusinessDaySettings();
  const [businessDayStartTime, setBusinessDayStartTime] = useState(initialSettings.startTime);
  const [businessDayEndTime, setBusinessDayEndTime] = useState(initialSettings.endTime);
  const [customTimeEnabled, setCustomTimeEnabled] = useState(initialSettings.enabled);

  // Sync with localStorage changes (when Settings tab updates values)
  useEffect(() => {
    if (!customReportingTime) {
      const handleStorageChange = () => {
        const startTime = localStorage.getItem('reports:businessDayStartTime') || '01:00';
        const endTime = localStorage.getItem('reports:businessDayEndTime') || '01:00';
        const enabled = localStorage.getItem('reports:customTimeEnabled') === 'true';
        setBusinessDayStartTime(startTime);
        setBusinessDayEndTime(endTime);
        setCustomTimeEnabled(enabled);
      };
      
      // Listen for storage events (when Settings tab updates in another tab/window)
      window.addEventListener('storage', handleStorageChange);
      // Also check periodically for same-tab updates (since storage event doesn't fire in same tab)
      const interval = setInterval(handleStorageChange, 1000);
      
      return () => {
        window.removeEventListener('storage', handleStorageChange);
        clearInterval(interval);
      };
    }
  }, [customReportingTime]);

  // Sync with external changes
  useEffect(() => {
    setInternalStartDate(startDate || null);
    setInternalEndDate(endDate || null);
  }, [startDate, endDate]);

  // Sync time values with external changes - commented out, time is read-only
  // useEffect(() => {
  //   if (startTime !== undefined) setInternalStartTime(startTime);
  //   if (endTime !== undefined) setInternalEndTime(endTime);
  // }, [startTime, endTime]);

  // Helper function to check if current date range matches a specific quick select option
  // Now uses pattern matching instead of exact date matching for better UX
  const refNow = new Date();
  const wtdPresetDisabled = refNow.getUTCDay() === 0;
  const mtdPresetDisabled = refNow.getUTCDate() === 1;
  const ytdPresetDisabled = refNow.getUTCMonth() === 0 && refNow.getUTCDate() === 1;

  const isQuickSelectActive = (type: 'last7' | 'last30' | 'last90' | 'today' | 'thisMonth' | 'wtd' | 'mtd' | 'ytd'): boolean => {
    if (!internalStartDate || !internalEndDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startDateNormalized = new Date(internalStartDate);
    startDateNormalized.setHours(0, 0, 0, 0);
    
    const endDateNormalized = new Date(internalEndDate);
    endDateNormalized.setHours(0, 0, 0, 0);

    // Calculate the difference in days between start and end
    const daysDifference = Math.round((endDateNormalized.getTime() - startDateNormalized.getTime()) / (1000 * 60 * 60 * 24));

    // Check if it's a month boundary (start is 1st of month, end is last day of same month) using UTC
    const isMonthBoundary = startDateNormalized.getUTCDate() === 1 && 
                           endDateNormalized.getUTCDate() === new Date(Date.UTC(endDateNormalized.getUTCFullYear(), endDateNormalized.getUTCMonth() + 1, 0)).getUTCDate() &&
                           startDateNormalized.getUTCMonth() === endDateNormalized.getUTCMonth() &&
                           startDateNormalized.getUTCFullYear() === endDateNormalized.getUTCFullYear();

    switch (type) {
      case 'last7': {
        // Check if it's approximately a 7-day range (6-8 days to allow for slight variations)
        return daysDifference >= 6 && daysDifference <= 8;
      }
      case 'last30': {
        // Check if it's approximately a 30-day range (28-32 days to account for month variations)
        // BUT exclude month boundaries (those should match "thisMonth" instead)
        return daysDifference >= 28 && daysDifference <= 32 && !isMonthBoundary;
      }
      case 'last90': {
        // Check if it's approximately a 90-day range (88-92 days to account for month variations)
        return daysDifference >= 88 && daysDifference <= 92;
      }
      case 'today': {
        // Check if start and end are the same day (0 day difference)
        return daysDifference === 0;
      }
      case 'thisMonth': {
        // Check if it matches current month boundaries using UTC
        const startOfMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1, 0, 0, 0, 0));
        const endOfMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 0, 23, 59, 59, 999));
        
        // Also check if it's any full month (start date is 1st, end date is last day of month)
        const isFullMonth = startDateNormalized.getUTCDate() === 1 && 
                           endDateNormalized.getUTCDate() === new Date(Date.UTC(endDateNormalized.getUTCFullYear(), endDateNormalized.getUTCMonth() + 1, 0)).getUTCDate();
        
        return isFullMonth || 
               (startDateNormalized.getTime() === startOfMonth.getTime() && 
                endDateNormalized.getTime() === endOfMonth.getTime());
      }
      case 'wtd': {
        const startOfWeek = getStartOfWeekSundayUtc(refNow);
        const endYesterday = getYesterdayEndUtc(refNow);
        return isSameUTCDay(startDateNormalized, startOfWeek) &&
               isSameUTCDay(endDateNormalized, endYesterday);
      }
      case 'mtd': {
        const startOfMonth = new Date(Date.UTC(refNow.getUTCFullYear(), refNow.getUTCMonth(), 1, 0, 0, 0, 0));
        const endYesterday = getYesterdayEndUtc(refNow);
        return isSameUTCDay(startDateNormalized, startOfMonth) &&
               isSameUTCDay(endDateNormalized, endYesterday);
      }
      case 'ytd': {
        const startOfYear = new Date(Date.UTC(refNow.getUTCFullYear(), 0, 1, 0, 0, 0, 0));
        const endYesterday = getYesterdayEndUtc(refNow);
        return isSameUTCDay(startDateNormalized, startOfYear) &&
               isSameUTCDay(endDateNormalized, endYesterday);
      }
      default:
        return false;
    }
  };

  // Helper function to check if current time range matches a specific preset
  // const isTimePresetActive = (preset: keyof typeof TIME_PRESETS): boolean => {
  //   const presetConfig = TIME_PRESETS[preset];
  //   return internalStartTime === presetConfig.startTime && 
  //          internalEndTime === presetConfig.endTime;
  // };

  // Validation function to check if date range is valid
  const validateDateRange = (start: Date | null, end: Date | null): boolean => {
    if (!start || !end) return true; // Allow partial dates
    const startNormalized = new Date(start);
    startNormalized.setHours(0, 0, 0, 0);
    const endNormalized = new Date(end);
    endNormalized.setHours(0, 0, 0, 0);
    return endNormalized >= startNormalized;
  };

  // Notify parent of validation state whenever dates change
  useEffect(() => {
    if (onValidationChange) {
      const isValid = validateDateRange(internalStartDate, internalEndDate);
      onValidationChange(isValid);
    }
  }, [internalStartDate, internalEndDate, onValidationChange]);

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = parseDateFromInput(event.target.value);
    setInternalStartDate(newStartDate);
    
    // Apply cross-midnight logic if both dates are set
    // Use skipIfSameDay=true to preserve single-day selections
    if (newStartDate && internalEndDate) {
      const isSameDay = isSameUTCDay(newStartDate, internalEndDate);
      const { startDate, endDate } = applyCrossMidnightLogic(newStartDate, internalEndDate, isSameDay);
      if (endDate !== internalEndDate) {
        setInternalEndDate(endDate);
      }
      onChange(startDate, endDate, internalStartTime, internalEndTime);
    } else {
      onChange(newStartDate, internalEndDate, internalStartTime, internalEndTime);
    }
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = parseDateFromInput(event.target.value);
    setInternalEndDate(newEndDate);
    
    // Apply cross-midnight logic if both dates are set
    // Use skipIfSameDay=true to preserve single-day selections
    if (internalStartDate && newEndDate) {
      const isSameDay = isSameUTCDay(internalStartDate, newEndDate);
      const { startDate, endDate } = applyCrossMidnightLogic(internalStartDate, newEndDate, isSameDay);
      if (endDate !== newEndDate) {
        setInternalEndDate(endDate);
      }
      onChange(startDate, endDate, internalStartTime, internalEndTime);
    } else {
      onChange(internalStartDate, newEndDate, internalStartTime, internalEndTime);
    }
  };

  // Time handlers commented out - time is now read-only
  // const handleStartTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   const newTime = event.target.value;
  //   setInternalStartTime(newTime);
  //   onChange(internalStartDate, internalEndDate, newTime, internalEndTime);
  // };

  // const handleEndTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   const newTime = event.target.value;
  //   setInternalEndTime(newTime);
  //   onChange(internalStartDate, internalEndDate, internalStartTime, newTime);
  // };

  // const handleTimePreset = (preset: keyof typeof TIME_PRESETS) => {
  //   const presetConfig = TIME_PRESETS[preset];
  //   setInternalStartTime(presetConfig.startTime);
  //   setInternalEndTime(presetConfig.endTime);
  //   onChange(internalStartDate, internalEndDate, presetConfig.startTime, presetConfig.endTime);
  // };

  const handleQuickDateRange = (days: number) => {
    const today = new Date();
    // Use UTC to ensure consistent behavior across timezones
    // FIX: Subtract (days - 1) to make it exactly N days inclusive
    // Example: For 7 days, we want Jan 8-14 (7 days), not Jan 7-14 (8 days)
    const endDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 23, 59, 59, 999));
    const startDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - (days - 1), 0, 0, 0, 0));
    
    setInternalStartDate(startDate);
    setInternalEndDate(endDate);
    onChange(startDate, endDate, internalStartTime, internalEndTime);
  };

  const handleToday = () => {
    const today = new Date();
    // Use UTC to ensure consistent behavior across timezones
    const startOfDay = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 0, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 23, 59, 59, 999));
    
    console.log(`🔍 [DateRangeFilter] Today:`, {
      startDate: startOfDay.toISOString(),
      endDate: endOfDay.toISOString()
    });
    setInternalStartDate(startOfDay);
    setInternalEndDate(endOfDay);
    onChange(startOfDay, endOfDay, internalStartTime, internalEndTime);
  };

  const handleThisMonth = () => {
    const today = new Date();
    // Use UTC to avoid timezone shifts
    const startOfMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1, 0, 0, 0, 0));
    const endOfMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 0, 23, 59, 59, 999));
    
    setInternalStartDate(startOfMonth);
    setInternalEndDate(endOfMonth);
    onChange(startOfMonth, endOfMonth, internalStartTime, internalEndTime);
  };

  const handleWTD = () => {
    const today = new Date();
    if (today.getUTCDay() === 0) return;
    const startOfWeek = getStartOfWeekSundayUtc(today);
    const endYesterday = getYesterdayEndUtc(today);
    setInternalStartDate(startOfWeek);
    setInternalEndDate(endYesterday);
    onChange(startOfWeek, endYesterday, internalStartTime, internalEndTime);
  };

  const handleMTD = () => {
    const today = new Date();
    if (today.getUTCDate() === 1) return;
    const startOfMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1, 0, 0, 0, 0));
    const endYesterday = getYesterdayEndUtc(today);
    setInternalStartDate(startOfMonth);
    setInternalEndDate(endYesterday);
    onChange(startOfMonth, endYesterday, internalStartTime, internalEndTime);
  };

  const handleYTD = () => {
    const today = new Date();
    if (today.getUTCMonth() === 0 && today.getUTCDate() === 1) return;
    const startOfYear = new Date(Date.UTC(today.getUTCFullYear(), 0, 1, 0, 0, 0, 0));
    const endYesterday = getYesterdayEndUtc(today);
    setInternalStartDate(startOfYear);
    setInternalEndDate(endYesterday);
    onChange(startOfYear, endYesterday, internalStartTime, internalEndTime);
  };

  const handleClear = () => {
    setInternalStartDate(null);
    setInternalEndDate(null);
    // Time is read-only, always 03:00-03:00
    onChange(null, null, '03:00', '03:00');
  };

  // Cross-midnight logic helper function
  // IMPORTANT: This should only be called when dates are explicitly changed by the user,
  // NOT when business day times change. Time changes should never modify the date range.
  const applyCrossMidnightLogic = (startDate: Date, endDate: Date, skipIfSameDay: boolean = false) => {
    if (!customTimeEnabled || !customReportingTime?.crossMidnightLogic) {
      return { startDate, endDate };
    }

    // If both dates are the same day and skipIfSameDay is true, don't apply cross-midnight logic
    // This prevents single-day selections from being modified
    if (skipIfSameDay) {
      if (isSameUTCDay(startDate, endDate)) {
        return { startDate, endDate };
      }
    }

    // Parse start and end times
    const [startHour, startMinute] = businessDayStartTime.split(':').map(Number);
    const [endHour, endMinute] = businessDayEndTime.split(':').map(Number);
    
    // Convert to minutes for comparison
    const startTimeMinutes = startHour * 60 + startMinute;
    const endTimeMinutes = endHour * 60 + endMinute;
    
    // If end time is <= start time, we cross midnight
    const crossesMidnight = endTimeMinutes <= startTimeMinutes;
    
    if (crossesMidnight) {
      // Only extend end date if dates are different (multi-day range)
      // For single-day selections, don't modify the date
      if (!isSameUTCDay(startDate, endDate)) {
        // Extend end date by 1 day for cross-midnight business days (multi-day ranges only)
        const adjustedEndDate = new Date(endDate);
        adjustedEndDate.setUTCDate(adjustedEndDate.getUTCDate() + 1);
        
        const formatUTCLog = (d: Date) => `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
        console.log(`🌙 [DateRangeFilter] Cross-midnight detected (multi-day):`, {
          original: `${formatUTCLog(startDate)} - ${formatUTCLog(endDate)}`,
          adjusted: `${formatUTCLog(startDate)} - ${formatUTCLog(adjustedEndDate)}`,
          businessHours: `${businessDayStartTime} - ${businessDayEndTime}`
        });
        
        return { startDate, endDate: adjustedEndDate };
      } else {
        // Single-day selection: don't modify dates even if cross-midnight logic applies
        const formatUTCLog = (d: Date) => `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
        console.log(`🌙 [DateRangeFilter] Cross-midnight detected but preserving single-day selection:`, {
          date: `${formatUTCLog(startDate)}`,
          businessHours: `${businessDayStartTime} - ${businessDayEndTime}`
        });
      }
    }
    
    return { startDate, endDate };
  };

  // Business day time handlers
  // IMPORTANT: These handlers should NEVER modify the date range.
  // Time changes are for business day configuration only, not for date filtering.
  const handleBusinessDayStartTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStartTime = event.target.value;
    setBusinessDayStartTime(newStartTime);
    // Notify parent of time change, but do NOT modify dates
    onReportingTimeChange?.(newStartTime, businessDayEndTime);
    // Explicitly do NOT call onChange or applyCrossMidnightLogic here
  };

  const handleBusinessDayEndTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEndTime = event.target.value;
    setBusinessDayEndTime(newEndTime);
    // Notify parent of time change, but do NOT modify dates
    onReportingTimeChange?.(businessDayStartTime, newEndTime);
    // Explicitly do NOT call onChange or applyCrossMidnightLogic here
  };

  const handleCustomTimeToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setCustomTimeEnabled(enabled);
    
    // When toggling custom time, preserve the current date selection
    // Only apply cross-midnight logic for multi-day ranges, not single-day selections
    if (enabled && internalStartDate && internalEndDate) {
      const isSameDay = isSameUTCDay(internalStartDate, internalEndDate);
      const { startDate, endDate } = applyCrossMidnightLogic(internalStartDate, internalEndDate, isSameDay);
      // Only update if the date actually changed (multi-day range)
      if (endDate.getTime() !== internalEndDate.getTime()) {
        setInternalEndDate(endDate);
        onChange(startDate, endDate);
      }
    }
  };

  const toDate = (d: Date | string | null): Date | null => {
    if (!d) return null;
    return typeof d === 'string' ? new Date(d) : d;
  };

  const formatDateRange = () => {
    const start = toDate(internalStartDate);
    const end = toDate(internalEndDate);
    
    // Helper to format date using UTC to match date picker behavior
    const formatUTCDate = (d: Date | null): string => {
      if (!d) return '';
      return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
    };
    
    if (!start && !end) return 'All dates';
    if (!start) return `Until ${formatUTCDate(end)}`;
    if (!end) return `From ${formatUTCDate(start)}`;
    if (start.getTime() === end.getTime()) {
      return formatUTCDate(start);
    }
    return `${formatUTCDate(start)} - ${formatUTCDate(end)}`;
  };

  return (
    <FilterContainer>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <DateRangeIcon sx={{ color: '#71717a', fontSize: '20px' }} />
        <Typography 
          variant="h6" 
          component="h3" 
          sx={{ 
            color: '#18181b',
            fontFamily: "'Inter', sans-serif",
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '24px'
          }}
        >
          {label}
        </Typography>
        <Chip 
          label={formatDateRange()} 
          size="small" 
          variant="outlined"
          sx={{ 
            ml: 'auto',
            fontFamily: "'Inter', sans-serif",
            fontWeight: 500,
            fontSize: '12px',
            lineHeight: '16px',
            color: '#18181b',
            borderColor: '#e4e4e7',
            backgroundColor: '#f4f4f5'
          }}
        />
      </Box>

      <DateInputsContainer
        onMouseDown={(e) => {
          // Prevent clicks inside date inputs from closing parent popovers
          e.stopPropagation();
        }}
        onClick={(e) => {
          // Prevent clicks inside date inputs from closing parent popovers
          e.stopPropagation();
        }}
      >
        <TextField
          label="Start Date"
          type="date"
          value={formatDateForInput(internalStartDate)}
          onChange={handleStartDateChange}
          disabled={disabled}
          size="small"
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            max: internalEndDate ? formatDateForInput(internalEndDate) : undefined
          }}
          sx={{ 
            minWidth: 150,
            fontFamily: "'Inter', sans-serif",
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff',
              color: '#18181b',
              borderRadius: '6px',
              border: '1px solid #e4e4e7',
              fontFamily: "'Inter', sans-serif",
              fontSize: '14px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e4e4e7',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#18181b',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#18181b',
                borderWidth: '1px',
              },
            },
            '& .MuiInputLabel-root': {
              fontFamily: "'Inter', sans-serif",
              color: '#71717a',
              '&.Mui-focused': {
                color: '#18181b',
              },
            },
          }}
          onFocus={(e) => {
            // Prevent focus events from bubbling
            e.stopPropagation();
          }}
          onBlur={(e) => {
            // Prevent blur events from closing parent popovers immediately
            // Only allow blur if clicking outside the entire date picker area
            e.stopPropagation();
          }}
        />
        
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#71717a',
            fontFamily: "'Inter', sans-serif",
            fontSize: '14px',
            lineHeight: '20px'
          }}
        >
          to
        </Typography>
        
        <TextField
          label="End Date"
          type="date"
          value={formatDateForInput(internalEndDate)}
          onChange={handleEndDateChange}
          disabled={disabled}
          size="small"
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            min: internalStartDate ? formatDateForInput(internalStartDate) : undefined
          }}
          sx={{ 
            minWidth: 150,
            fontFamily: "'Inter', sans-serif",
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff',
              color: '#18181b',
              borderRadius: '6px',
              border: '1px solid #e4e4e7',
              fontFamily: "'Inter', sans-serif",
              fontSize: '14px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e4e4e7',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#18181b',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#18181b',
                borderWidth: '1px',
              },
            },
            '& .MuiInputLabel-root': {
              fontFamily: "'Inter', sans-serif",
              color: '#71717a',
              '&.Mui-focused': {
                color: '#18181b',
              },
            },
          }}
          onFocus={(e) => {
            // Prevent focus events from bubbling
            e.stopPropagation();
          }}
          onBlur={(e) => {
            // Prevent blur events from closing parent popovers immediately
            // Only allow blur if clicking outside the entire date picker area
            e.stopPropagation();
          }}
        />

        <Button
          variant="outlined"
          size="small"
          onClick={handleClear}
          disabled={disabled || (!internalStartDate && !internalEndDate)}
          sx={{ 
            ml: 1,
            fontFamily: "'Inter', sans-serif",
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '20px',
            textTransform: 'none',
            color: '#18181b',
            borderColor: '#e4e4e7',
            borderRadius: '6px',
            '&:hover': {
              borderColor: '#18181b',
              backgroundColor: '#fafafa'
            },
            '&:disabled': {
              color: '#71717a',
              borderColor: '#e4e4e7',
            }
          }}
        >
          Clear
          </Button>
        </DateInputsContainer>
        
        {/* Error message when date range is invalid */}
        {!validateDateRange(internalStartDate, internalEndDate) && internalStartDate && internalEndDate && (
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              color: '#dc2626',
              fontFamily: "'Inter', sans-serif",
              fontSize: '12px',
              lineHeight: '16px'
            }}
          >
            end date must be greater than start date
          </Typography>
        )}

        {/* Report Day - Read-only display of cutoff times */}
        <Typography 
          variant="body2" 
          sx={{ 
            mt: 1.5,
            color: '#71717a',
            fontFamily: "'Inter', sans-serif",
            fontSize: '14px',
            lineHeight: '20px'
          }}
        >
          Report Day: {internalStartTime} to {internalEndTime}
        </Typography>

        {/* Time Range Quick Buttons - Commented out */}
        {/* {showQuickButtons && (
          <QuickButtonsContainer sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ color: 'grey.400', mr: 1 }}>
              Time presets:
            </Typography>
            
            {(Object.keys(TIME_PRESETS) as Array<keyof typeof TIME_PRESETS>).map((preset) => {
              const presetConfig = TIME_PRESETS[preset];
              const isActive = isTimePresetActive(preset);
              
              return (
                <Button
                  key={preset}
                  variant={isActive ? "contained" : "outlined"}
                  size="small"
                  onClick={() => handleTimePreset(preset)}
                  disabled={disabled}
                  startIcon={<TimeIcon />}
                  sx={isActive ? { 
                    backgroundColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    }
                  } : {
                    color: 'grey.300',
                    borderColor: 'grey.600',
                    '&:hover': {
                      borderColor: 'grey.400',
                      backgroundColor: 'grey.800'
                    }
                  }}
                >
                  {presetConfig.label}
                </Button>
              );
            })}
          </QuickButtonsContainer>
        )} */}

        {showQuickButtons && (
        <>
          <Divider sx={{ my: 2, borderColor: '#e4e4e7' }} />
          <QuickButtonsContainer>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#71717a',
                fontFamily: "'Inter', sans-serif",
                fontSize: '14px',
                lineHeight: '20px',
                mr: 1 
              }}
            >
              Quick select:
            </Typography>
            
            <Button
              variant={isQuickSelectActive('last7') ? "contained" : "outlined"}
              size="small"
              onClick={() => handleQuickDateRange(7)}
              disabled={disabled}
              startIcon={<DateRangeIcon />}
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                textTransform: 'none',
                borderRadius: '6px',
                ...(isQuickSelectActive('last7') ? { 
                  backgroundColor: '#18181b',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#27272a',
                  }
                } : {
                  color: '#18181b',
                  borderColor: '#e4e4e7',
                  '&:hover': {
                    borderColor: '#18181b',
                    backgroundColor: '#fafafa'
                  }
                })
              }}
            >
              Last 7 Days
            </Button>
            
            <Button
              variant={isQuickSelectActive('last30') ? "contained" : "outlined"}
              size="small"
              onClick={() => handleQuickDateRange(30)}
              disabled={disabled}
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                textTransform: 'none',
                borderRadius: '6px',
                ...(isQuickSelectActive('last30') ? { 
                  backgroundColor: '#18181b',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#27272a',
                  }
                } : {
                  color: '#18181b',
                  borderColor: '#e4e4e7',
                  '&:hover': {
                    borderColor: '#18181b',
                    backgroundColor: '#fafafa'
                  }
                })
              }}
            >
              Last 30 Days
            </Button>
            
            <Button
              variant={isQuickSelectActive('last90') ? "contained" : "outlined"}
              size="small"
              onClick={() => handleQuickDateRange(90)}
              disabled={disabled}
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                textTransform: 'none',
                borderRadius: '6px',
                ...(isQuickSelectActive('last90') ? { 
                  backgroundColor: '#18181b',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#27272a',
                  }
                } : {
                  color: '#18181b',
                  borderColor: '#e4e4e7',
                  '&:hover': {
                    borderColor: '#18181b',
                    backgroundColor: '#fafafa'
                  }
                })
              }}
            >
              Last 90 Days
            </Button>
            
            <Button
              variant={isQuickSelectActive('today') ? "contained" : "outlined"}
              size="small"
              onClick={handleToday}
              disabled={disabled}
              startIcon={<TodayIcon />}
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                textTransform: 'none',
                borderRadius: '6px',
                ...(isQuickSelectActive('today') ? { 
                  backgroundColor: '#18181b',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#27272a',
                  }
                } : {
                  color: '#18181b',
                  borderColor: '#e4e4e7',
                  '&:hover': {
                    borderColor: '#18181b',
                    backgroundColor: '#fafafa'
                  }
                })
              }}
            >
              Today
            </Button>
            
            <Button
              variant={isQuickSelectActive('thisMonth') ? "contained" : "outlined"}
              size="small"
              onClick={handleThisMonth}
              disabled={disabled}
              startIcon={<CalendarIcon />}
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                textTransform: 'none',
                borderRadius: '6px',
                ...(isQuickSelectActive('thisMonth') ? { 
                  backgroundColor: '#18181b',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#27272a',
                  }
                } : {
                  color: '#18181b',
                  borderColor: '#e4e4e7',
                  '&:hover': {
                    borderColor: '#18181b',
                    backgroundColor: '#fafafa'
                  }
                })
              }}
            >
              This Month
            </Button>
            
            <Button
              variant={isQuickSelectActive('wtd') ? "contained" : "outlined"}
              size="small"
              onClick={handleWTD}
              disabled={disabled || wtdPresetDisabled}
              title={wtdPresetDisabled ? 'Not available on the first day of the week (UTC Sunday)' : undefined}
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                textTransform: 'none',
                borderRadius: '6px',
                ...(isQuickSelectActive('wtd') ? { 
                  backgroundColor: '#18181b',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#27272a',
                  }
                } : {
                  color: '#18181b',
                  borderColor: '#e4e4e7',
                  '&:hover': {
                    borderColor: '#18181b',
                    backgroundColor: '#fafafa'
                  }
                })
              }}
            >
              WTD
            </Button>
            
            <Button
              variant={isQuickSelectActive('mtd') ? "contained" : "outlined"}
              size="small"
              onClick={handleMTD}
              disabled={disabled || mtdPresetDisabled}
              title={mtdPresetDisabled ? 'Not available on the first day of the month (UTC)' : undefined}
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                textTransform: 'none',
                borderRadius: '6px',
                ...(isQuickSelectActive('mtd') ? { 
                  backgroundColor: '#18181b',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#27272a',
                  }
                } : {
                  color: '#18181b',
                  borderColor: '#e4e4e7',
                  '&:hover': {
                    borderColor: '#18181b',
                    backgroundColor: '#fafafa'
                  }
                })
              }}
            >
              MTD
            </Button>
            
            <Button
              variant={isQuickSelectActive('ytd') ? "contained" : "outlined"}
              size="small"
              onClick={handleYTD}
              disabled={disabled || ytdPresetDisabled}
              title={ytdPresetDisabled ? 'Not available on the first day of the year (UTC Jan 1)' : undefined}
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                textTransform: 'none',
                borderRadius: '6px',
                ...(isQuickSelectActive('ytd') ? { 
                  backgroundColor: '#18181b',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#27272a',
                  }
                } : {
                  color: '#18181b',
                  borderColor: '#e4e4e7',
                  '&:hover': {
                    borderColor: '#18181b',
                    backgroundColor: '#fafafa'
                  }
                })
              }}
            >
              YTD
            </Button>
          </QuickButtonsContainer>
        </>
      )}

      {/* Custom Business Day Hours Section */}
      {customReportingTime && (
        <BusinessHoursContainer>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <SettingsIcon sx={{ color: 'grey.400', fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600 }}>
              Business Day Configuration
            </Typography>
          </Box>
          
          <FormControlLabel
            control={
              <Switch
                checked={customTimeEnabled}
                onChange={handleCustomTimeToggle}
                disabled={disabled}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'primary.main',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: 'primary.main',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: 'grey.300' }}>
                Enable Custom Reporting Hours
              </Typography>
            }
            sx={{ mb: 1 }}
          />

          {customTimeEnabled && (
            <>
              <Typography variant="body2" sx={{ color: 'grey.400', mb: 1 }}>
                {customReportingTime.description}
              </Typography>
              
              <TimePickerContainer>
                <TextField
                  label="Business Day Start"
                  type="time"
                  value={businessDayStartTime}
                  onChange={handleBusinessDayStartTimeChange}
                  disabled={disabled}
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                    sx: { color: 'grey.400' }
                  }}
                  InputProps={{
                    sx: { 
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'grey.600',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'grey.400',
                      },
                    }
                  }}
                  sx={{ minWidth: 150 }}
                />
                
                <Typography variant="body2" sx={{ color: 'grey.400' }}>
                  to
                </Typography>
                
                <TextField
                  label="Business Day End"
                  type="time"
                  value={businessDayEndTime}
                  onChange={handleBusinessDayEndTimeChange}
                  disabled={disabled}
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                    sx: { color: 'grey.400' }
                  }}
                  InputProps={{
                    sx: { 
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'grey.600',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'grey.400',
                      },
                    }
                  }}
                  sx={{ minWidth: 150 }}
                />

                {businessDayStartTime >= businessDayEndTime && (
                  <Chip 
                    icon={<TimeIcon />}
                    label="Cross-Midnight" 
                    size="small" 
                    color="warning"
                    variant="outlined"
                    sx={{ 
                      color: 'orange',
                      borderColor: 'orange',
                      backgroundColor: 'rgba(255, 165, 0, 0.1)'
                    }}
                  />
                )}
              </TimePickerContainer>
            </>
          )}
        </BusinessHoursContainer>
      )}
    </FilterContainer>
  );
}; 